import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, defer, map, shareReplay } from 'rxjs';

import type { ApiEnvelope } from '../http/api-envelope';
import type { CountryResponse } from './countries.contract';
import { toCountries } from './countries.mapper';
import type { Country } from './country.model';

/**
 * The ISO 3166-1 countries a company can be in. Doubles as its DI token.
 *
 * Lives in core because the selector that shows them is shared.
 */
@Injectable()
export abstract class CountriesApi {
  abstract list(): Observable<readonly Country[]>;
}

/**
 * `GET /workspaces/countries` — 250 rows sorted by English name.
 *
 * The table is static on the backend, so it is fetched once per session and
 * replayed to every later caller (the "no caching" rule is about workspace
 * data, which this is not). A failed request is not remembered: the next
 * caller asks again.
 */
@Injectable()
export class HttpCountriesApi extends CountriesApi {
  private readonly http = inject(HttpClient);

  private readonly countries$ = defer(() =>
    this.http.get<ApiEnvelope<readonly CountryResponse[] | null>>('/workspaces/countries'),
  ).pipe(
    map((response) => toCountries(response.data)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  override list(): Observable<readonly Country[]> {
    return this.countries$;
  }
}
